import { useMemo, useState } from 'react';
import Image from 'next/image';
import { Link } from '~/components/global';
import { useInterval } from 'usehooks-ts';
import dayjs, { Dayjs } from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import useExperiment from '~/lib/experiment';
import useUser from '~/lib/user';
import useSumoDay from '~/lib/util/sumoDay';
import PlusLogo from '../../../public/as-plus-2.svg';
import EmailLove from '../../../public/email-love.svg';
import ShopExclusives from '../../../public/shop-exclusives.svg';
import CommentDots from '../../../public/comment-dots.svg';
import Refresh from '../../../public/refresh.svg';
import Hype from '../../../public/hype.svg';
import { useRouter } from 'next/router';
import {
  ASKSUMO_CLEANUP_EXPERIMENT_NAME,
  SWAG_PROMO_EXPERIMENT_NAME,
  SWAG_PROMO_CAMPAIGN_NAME,
} from '~/lib/util/constants';
import { SessionObjectProps } from '~/types/user';
import { getPlusDiscount } from '~/lib/util/helpers';

dayjs.extend(relativeTime);

interface CampaignTimerButtonProps {
  label: string;
  href: string;
  endDate: string;
}

function AskSumo() {
  const router = useRouter();
  const { user } = useUser();
  const { isSumoDay } = useSumoDay();
  const isNewBuyer = useMemo(
    () => !(user?.number_of_purchases && user?.number_of_purchases > 0),
    [user],
  );
  const hasAskedQuestion = useMemo(
    () => router.pathname === '/asksumo' && !!router.query.question,
    [router.pathname, router.query.question],
  );

  return (
    <Link
      href="/asksumo/?ref=asksumo-navitem"
      className="flex items-center justify-center whitespace-nowrap rounded-[40px] bg-gradient-to-r from-[#BCF2CB] to-[#AEDBF4] px-5 py-1"
    >
      <Image
        src={hasAskedQuestion ? Refresh : CommentDots}
        alt="Ask Sumo"
        className="mr-[5px] align-middle"
      />
      {hasAskedQuestion ? (
        <>
          <strong>AskSumo</strong>{' '}
          <span className="md:max-lg:hidden">&nbsp;a new question</span>
        </>
      ) : isNewBuyer && !isSumoDay ? (
        <>
          <strong>AskSumo</strong>{' '}
          <span className="md:max-lg:hidden">&nbsp;and get 10% off</span>
        </>
      ) : (
        <>
          <span className="md:max-lg:hidden">
            Introducing&nbsp;<strong>AskSumo</strong>, try it now
          </span>
          <span className="max-md:hidden lg:hidden">
            Try <strong>AskSumo</strong>
          </span>
        </>
      )}
    </Link>
  );
}

function SwagPromo() {
  return (
    <Link
      href="https://appsumo.com/sumoday/giveaway?ref=giveawaynav"
      target="_blank"
      className="flex items-center justify-center rounded-[40px] bg-[#5ef46b]/[15%] px-5 py-1 text-black-pearl hover:bg-[#5ef46b]/30"
    >
      <Image
        src={Hype}
        alt="swag-promo"
        className="mr-[5px] align-middle"
        height={16}
        width={16}
      />
      <strong className="whitespace-nowrap">Win custom swag</strong>
    </Link>
  );
}

function PlusDiscount({ session }: { session?: SessionObjectProps }) {
  return (
    <Link
      href="/plus/?ref=Plusnav"
      className="flex items-center justify-center rounded-[40px] bg-dorado-light px-5 py-1 text-black-pearl"
    >
      <Image src={PlusLogo} alt="Save 10%" className="mr-[5px] align-middle" />
      <strong className="whitespace-nowrap">
        Save {getPlusDiscount(session)}%
      </strong>
    </Link>
  );
}

function FreeIdeas() {
  return (
    <Link
      href="/content/75-free-business-ideas?ref=nav"
      className="flex items-center justify-center whitespace-nowrap rounded-[40px] bg-blue-300 px-5 py-1 text-black-pearl hover:bg-blue-400"
    >
      <Image
        src={EmailLove}
        height={16}
        alt="Get FREE biz ideas"
        className="mr-[5px] align-middle"
      />
      <strong className="">Get FREE biz ideas</strong>
    </Link>
  );
}

function ShopPlusExclusives() {
  return (
    <Link
      href="/collections/plus-exclusives/?ref=PEnav"
      className="flex items-center justify-center whitespace-nowrap rounded-[40px] bg-bolt/20 px-5 py-1"
    >
      <Image
        src={ShopExclusives}
        alt="Shop Plus exclusives"
        className="mr-[5px] h-6 align-middle"
      />
      <strong className="">Shop Plus exclusives</strong>
    </Link>
  );
}

function CampaignTimerButton({
  label,
  href,
  endDate,
}: CampaignTimerButtonProps) {
  const [now, setNow] = useState<Dayjs>(dayjs());

  useInterval(() => {
    setNow(dayjs());
  }, 1000);

  const timeRemaining = useMemo(() => {
    const end = dayjs(endDate);
    let seconds = end.diff(now, 'second');
    if (seconds > 0) {
      const hours = Math.floor(seconds / 3600);
      const mins = Math.floor(seconds / 60) % 60;
      seconds %= 60;
      const parts = [];
      if (hours) {
        parts.push(String(hours).padStart(2, '0'));
      }
      parts.push(String(mins).padStart(2, '0'));
      if (hours || mins || seconds) {
        parts.push(String(seconds).padStart(2, '0'));
      }
      return parts.join(':');
    }

    return '00:00';
  }, [endDate, now]);

  return (
    <Link
      href={`${href}`}
      className="flex items-center justify-center rounded-[40px] bg-bolt/20 px-5 py-1"
    >
      <Image
        src={ShopExclusives}
        alt={label}
        className="mr-[5px] align-middle"
      />
      <strong className="">{label}</strong>
      <span className="ml-1.5">{timeRemaining}</span>
    </Link>
  );
}

export default function NavPromo() {
  const { session, user, isLoading: isLoadingUser } = useUser();
  const { button, campaigns } = session || {};

  const { variant: askSumoNavItem, isLoading: isLoadingExperiment } =
    useExperiment(ASKSUMO_CLEANUP_EXPERIMENT_NAME);

  const swagCampaignActive = useMemo(() => {
    return campaigns?.includes(SWAG_PROMO_CAMPAIGN_NAME);
  }, [campaigns]);

  const { variant: swagPromoNavItem } = useExperiment(
    SWAG_PROMO_EXPERIMENT_NAME,
    !!user || !swagCampaignActive, // skip enroll if auth or swag campaign not active
  );

  const isCampaign = useMemo(() => {
    if (button?.end_date) {
      const now = dayjs();
      const ends = dayjs(button.end_date);
      return now < ends;
    }

    return false;
  }, [button?.end_date]);

  const hasUser = useMemo(() => !!user, [user]);
  const hasPlus = useMemo(() => user && user?.has_plus, [user]);
  const isLoading = isLoadingUser || isLoadingExperiment;

  const EXPERIMENT_ASKSUMO_NAVITEM_ENABLE = askSumoNavItem === 'enabled';
  const EXPERIMENT_SWAG_PROMO_ENABLE =
    swagPromoNavItem === 'enabled' && swagCampaignActive;

  if (isLoading) {
    return <div />;
  }

  let buttonEl = null;
  if (isCampaign && button) {
    // campaign button overrides all other buttons
    buttonEl = (
      <CampaignTimerButton
        label={button.label}
        href={button.link}
        endDate={button.end_date}
      />
    );
  } else if (!hasUser) {
    if (EXPERIMENT_SWAG_PROMO_ENABLE) {
      buttonEl = <SwagPromo />;
    } else if (!EXPERIMENT_ASKSUMO_NAVITEM_ENABLE) {
      // if anonymous and not in askSumo test, show askSumo button
      buttonEl = <AskSumo />;
    } else {
      // if anonymous, show free ideas button
      buttonEl = <FreeIdeas />;
    }
  } else if (hasUser && !hasPlus) {
    // if user is not a plus member, show save %off button (now dynamic)
    buttonEl = <PlusDiscount session={session} />;
  } else if (hasUser && hasPlus) {
    // if user is a plus member, show shop plus exclusives button
    buttonEl = <ShopPlusExclusives />;
  }

  // As long the AskSumo navItem is present we need to hide the pill between 768px and 1024px
  // to avoid overlapping with the nav items, using the class `md:max-lg:hidden`
  // see components/navigation/components/NavGroups.tsx for AskSumo nav item.
  return (
    <div className="my-6 w-full md:my-0 md:w-auto md:max-lg:hidden">
      {buttonEl}
    </div>
  );
}
